import type { Category, Story } from '@prezly/sdk';
import type { LocaleObject } from '@prezly/theme-kit-core';
import type { PaginationProps } from '@prezly/theme-kit-nextjs';
import { getResolvedPath, useCurrentLocale, useInfiniteLoading } from '@prezly/theme-kit-nextjs';
import { useEffect } from 'react';

async function fetchStories<T extends Story = Story>(
    page: number,
    pageSize: number,
    withHighlightedStory: boolean,
    category?: Category,
    locale?: LocaleObject,
    include?: (keyof Story.ExtraFields)[],
    filterQuery?: Object,
): Promise<{ stories: T[]; storiesTotal: number }> {
    const result = await fetch(getResolvedPath('/api/fetch-stories'), {
        method: 'POST',
        headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            page,
            pageSize,
            withHighlightedStory,
            category,
            include,
            ...(locale && {
                localeCode: locale.toUnderscoreCode(),
            }),
            filterQuery,
        }),
    });

    if (!result.ok) {
        const { message } = await result.json();
        throw new Error(message);
    }

    return result.json();
}

/**
 * This is the exact copy of the code from
 * https://github.com/prezly/theme-kit-js/blob/33d61f413e31e1bef854bdca6596d91242a0c68e/packages/nextjs/src/infinite-loading/useInfiniteStoriesLoading.ts#L79-L83
 *
 * BUT with a modification:
 * - the `resetData` function is no longer a dependency of the `useEffect()` hook.
 *
 * ---
 *
 * NOTE: This hook depends on the presence of a Next API route (/api/fetch-stories)!
 * The handler for this route is exported from `apiHandlers/fetchStories.ts` file.
 * See https://github.com/prezly/theme-nextjs-bea/blob/d381e9878187ca98815d240d3ffdf10a75993c14/pages/index.tsx#L24 for usage example
 */
export function useInfiniteStoriesLoading<T extends Story = Story>(
    initialStories: T[],
    pagination: PaginationProps,
    category?: Category,
    include?: (keyof Story.ExtraFields)[],
    filterQuery?: Object,
) {
    const currentLocale = useCurrentLocale();
    const { withHighlightedStory = false } = pagination;

    const { canLoadMore, data, isLoading, loadMore, resetData } = useInfiniteLoading<T>({
        fetchingFn: async (nextPage, pageSize) => {
            const { stories } = await fetchStories<T>(
                nextPage,
                pageSize,
                withHighlightedStory,
                category,
                currentLocale,
                include,
                filterQuery,
            );
            return stories;
        },
        initialData: initialStories,
        pagination,
    });

    useEffect(() => {
        if (category?.id) {
            resetData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category?.id]);

    return {
        canLoadMore,
        isLoading,
        loadMoreStories: loadMore,
        stories: data,
    };
}
